import { useCallback, useEffect, useState } from 'react';

/**
 * 정산년월은 **한 번 고르면 그대로 남는다** — 화면을 옮기거나 새로 열어도 같은 달로 돌아온다.
 *
 * 왜: 담당자는 한 달을 여러 화면에서 처리한다(청구 → 재무추가 → 월정기 → 영업정산 → 명세 생성).
 * 화면마다 최신 월로 되돌아가면 매번 다시 고르게 되고, **고르는 걸 잊으면 남의 달을 고친다.**
 * 그래서 저장 위치도 화면별이 아니라 **하나**다.
 *
 * ⚠️ 이건 기기(브라우저)에 남는다. 같은 사람이 다른 PC 에서 열면 그 PC 의 기억을 따른다 —
 * "나만 다른 달이 뜬다"는 신고가 오면 여기가 답이다. 그래서 화면에 **기억됐다는 표시와
 * 해제 버튼**을 같이 둔다(`StickyMonNote`). 조용히 남는 상태는 나중에 원인을 못 찾는다.
 */
const KEY = 'businweb.driveMon';
const EVENT = 'businweb:drivemon';

/** 정산년월의 모양. 이걸 통과하지 못하는 값은 저장하지 않는다. */
const MON = /^\d{4}-\d{2}$/;

export function readDriveMon(): string {
  try {
    const v = localStorage.getItem(KEY) ?? '';
    return MON.test(v) ? v : '';
  } catch {
    // 사생활 보호 모드 등에서 localStorage 접근 자체가 던진다. 기억이 없는 것과 같게 다룬다.
    return '';
  }
}

function urlDriveMon(): string {
  try {
    const v = new URLSearchParams(window.location.search).get('mon') ?? '';
    return MON.test(v) ? v : '';
  } catch {
    return '';
  }
}

function writeDriveMon(m: string): void {
  try {
    if (MON.test(m)) localStorage.setItem(KEY, m);
  } catch {
    /* 기억하지 못해도 화면은 그대로 돌아가야 한다 */
  }
}

/**
 * 기억된 정산년월과 그 setter. `setMon` 은 **`YYYY-MM` 일 때만 기억에 쓴다** —
 * 재무추가의 `이월잔액`(`drive_mon=''`) 같은 값은 정산년월이 아니라 **필터 모드**라서
 * 다른 화면으로 옮겨 가면 안 된다. 그 화면에서만 유효하고 나오면 마지막 실제 월로 돌아온다.
 */
export function useStickyDriveMon(): [string, (m: string) => void] {
  // 다른 화면에서 건너온 링크(`?mon=YYYY-MM` · lib/deepLink.ts)면 그 달부터 — 기억에도 쓴다(그 달을 보러 온 것이다).
  // 첫 렌더 값이어야 한다: 화면들이 마운트 때 `pickDriveMon(months, mon)` 으로 이 값을 한 번 읽는다(effect 로 바꾸면 늦다).
  const [mon, setMonState] = useState(() => {
    const u = urlDriveMon();
    if (u) { writeDriveMon(u); return u; }
    return readDriveMon();
  });
  const setMon = useCallback((m: string) => {
    setMonState(m);
    writeDriveMon(m);
    if (MON.test(m)) window.dispatchEvent(new CustomEvent<string>(EVENT, { detail: m }));
  }, []);
  // 화면 탭(#168)으로 살려 둔 화면도 **다른 화면에서 고른 달을 따라온다** — 안 따라오면 돌아와서 옛 달을 고친다.
  useEffect(() => {
    const on = (e: Event) => {
      const m = (e as CustomEvent<string>).detail;
      if (MON.test(m)) setMonState(m);
    };
    window.addEventListener(EVENT, on);
    return () => window.removeEventListener(EVENT, on);
  }, []);
  return [mon, setMon];
}

/**
 * 목록을 받은 뒤 어느 달을 띄울지 고른다.
 *
 * 기억된 달이 **그 화면의 목록에 실제로 있을 때만** 쓴다. 없으면 최신 월로 떨어진다 —
 * 기억을 그대로 밀어넣으면 화면이 **이유 없이 빈 상태**가 되고, 사용자는 데이터가 사라졌다고 읽는다.
 * (화면마다 월 목록이 다르다: 재무추가에 있는 달이 월정기에는 없을 수 있다.)
 */
export function pickDriveMon(months: readonly string[], remembered: string): string {
  if (remembered && months.includes(remembered)) return remembered;
  return months[0] ?? '';
}
